/**
 * LedgerTab — unified financial-history view replacing the old Expenses /
 * Recurring / Fund tabs. Filter chips switch between three feeds.
 *
 * "Ledger" is the right metaphor: every money fact lives here, the user
 * picks the lens. Default view is `expenses` (the most-used feed historically).
 *
 * The Recurring chip is hidden when the group type doesn't use recurring
 * expenses (Trip / Event).
 */
import { useState, useMemo } from 'react';
import { Receipt, RefreshCw, Wallet } from 'lucide-react';
import { TabsContent } from '../../components/ui/tabs';
import ExpensesTab from './ExpensesTab';
import RecurringTab from './RecurringTab';
import FundTab from './FundTab';
import useGroupTypeConfig from '../../hooks/useGroupTypeConfig';

export default function LedgerTab({ ctx }) {
  const typeConfig = useGroupTypeConfig(ctx?.group);
  const filters = useMemo(() => {
    const base = [
      { id: 'expenses', label: 'Expenses', Icon: Receipt },
      { id: 'fund',     label: 'Fund',     Icon: Wallet },
    ];
    if (typeConfig.features?.recurring) {
      base.push({ id: 'recurring', label: 'Recurring', Icon: RefreshCw });
    }
    return base;
  }, [typeConfig.features?.recurring]);

  const [filter, setFilter] = useState('expenses');
  const activeFilter = filters.find((f) => f.id === filter) ? filter : 'expenses';

  return (
    <TabsContent value="ledger" className="space-y-3">
      <div
        className="flex gap-1.5 p-1 rounded-2xl bg-muted/60 border border-border"
        data-testid="ledger-filter-chips"
        role="tablist"
        aria-label="Ledger filter"
      >
        {filters.map(({ id, label, Icon }) => {
          const active = activeFilter === id;
          return (
            <button
              key={id}
              data-testid={`ledger-filter-${id}`}
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(id)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition active:scale-[0.97] ${
                active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
              {label}
            </button>
          );
        })}
      </div>

      {activeFilter === 'expenses'  && <ExpensesTab  ctx={ctx} />}
      {activeFilter === 'recurring' && <RecurringTab ctx={ctx} />}
      {activeFilter === 'fund'      && <FundTab      ctx={ctx} view="transactions" />}
    </TabsContent>
  );
}
